import React, { createContext, useContext, useReducer, ReactNode } from 'react';

/// 検索カテゴリの種別
export type SearchCategory = 'all' | 'videos';

/// ソート順の型定義
export type SortBy =
  | 'date_desc'
  | 'date_asc'
  | 'views_desc'
  | 'views_asc'
  | 'likes_desc'
  | 'likes_asc';

/// 日付範囲の型定義
export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

/// 検索状態の型定義
export interface SearchState {
  keyword: string;
  category: SearchCategory;
  dateRange: DateRange;
  sortBy: SortBy;
  isLoading: boolean;
  selectedDate: Date | null;
}

/// 検索アクションの型定義
export type SearchAction =
  | { type: 'SET_KEYWORD'; payload: string }
  | { type: 'SET_CATEGORY'; payload: SearchCategory }
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_SORT_BY'; payload: SortBy }
  | { type: 'SET_SELECTED_DATE'; payload: Date | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET_FILTERS' };

/// 初期状態
const initialState: SearchState = {
  keyword: '',
  category: 'all',
  dateRange: { startDate: null, endDate: null },
  sortBy: 'date_desc',
  isLoading: false,
  selectedDate: null,
};

/// 検索状態のリデューサー
const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case 'SET_KEYWORD':
      return { ...state, keyword: action.payload };
    case 'SET_CATEGORY':
      return { ...state, category: action.payload };
    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.payload };
    case 'SET_SORT_BY':
      return { ...state, sortBy: action.payload };
    case 'SET_SELECTED_DATE':
      return { ...state, selectedDate: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET_FILTERS':
      return {
        ...initialState,
        isLoading: state.isLoading,
      };
    default:
      return state;
  }
};

/// コンテキストの型定義
interface SearchContextType {
  state: SearchState;
  dispatch: React.Dispatch<SearchAction>;
}

/// 検索コンテキスト
const SearchContext = createContext<SearchContextType | undefined>(undefined);

/// 検索プロバイダーのProps
interface SearchProviderProps {
  children: ReactNode;
}

/// 検索プロバイダーコンポーネント
export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(searchReducer, initialState);

  return (
    <SearchContext.Provider value={{ state, dispatch }}>
      {children}
    </SearchContext.Provider>
  );
};

/// 検索コンテキストを使用するカスタムフック
export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}; 